import { Injectable, effect, Signal, inject } from '@angular/core';
import { FormArray, FormGroup, NonNullableFormBuilder, Validators } from '@angular/forms';
import { formCombined } from '@r1-compass-library/utilities';
import { signalSlice } from 'ngxtension/signal-slice';
import { Observable, map, switchMap, zip, forkJoin, of, from, lastValueFrom } from 'rxjs';
import { FormControlStatus } from '@angular/forms';
import { DataMartService, SalesforceService } from '@r1-compass-library/services';
import {
    DataMartField,
    DataMartTable,
    DefinedField,
    DivisionConfig,
    DivisionConfigData,
    DivisionDataMappingSettings,
    DivisionMappingRow,
    DivisionMappingSettings,
    PredefinedField,
    predefinedFields,
    RequiredDivisionConfigData,
    SalesforceField,
    SalesforceListFieldResponse,
    SalesforceUniqueFieldTarget,
} from '@r1-compass-library/models';
import { GlobalDivisionsStore } from '@r1-compass-library/store/divisions/global-divisions-store';
import { GlobalLoggedInUserStore } from '@r1-compass-library/store/logged-in-user/global-logged-in-user.store';
import { DivisionDataMappingFormValidatorsService } from './division-data-mapping-form-validators.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { DivisionSettingsFormSliceState, FormLifecycleService } from '../form-lifecycle.service';
import {
    DataMappingFormValue,
    DivisionDataMappingFieldMappingFormValue,
    DivisionDataMappingForm,
    FieldMappingForm,
} from './form.model';

interface DivisionDataMappingFormSliceState extends DivisionSettingsFormSliceState<DataMappingFormValue> {
    playFieldsOptions: SalesforceListFieldResponse | undefined;
    dataMartTable: DataMartTable | undefined;
}

@Injectable()
export class DivisionDataMappingFormService {
    readonly #fb = inject(NonNullableFormBuilder);
    readonly #formLifecycleService = inject(FormLifecycleService);
    readonly #dataMartService = inject(DataMartService);
    readonly #salesforceService = inject(SalesforceService);
    readonly #validators = inject(DivisionDataMappingFormValidatorsService);
    readonly #globalDivisionStore = inject(GlobalDivisionsStore);
    readonly #globalLoggedInUserStore = inject(GlobalLoggedInUserStore);

    public divisionDataMappingForm: FormGroup<DivisionDataMappingForm> = this.#fb.group({
        fieldMapping: this.#fb.array<FormGroup<FieldMappingForm>>([]),
    });

    private initialFormState: DivisionDataMappingFormSliceState = {
        value: this.divisionDataMappingForm.getRawValue(),
        status: 'PENDING',
        loaded: false,
        dataMartTableOptions: [],
        salesforceObjectOptions: [],
        recommendationFieldsOptions: undefined,
        playFieldsOptions: undefined,
        selectedDivisionConfig: undefined,
        dataMartTable: undefined,
    };

    private selectedDivisionConfig$ = toObservable(this.#globalLoggedInUserStore.selectedDivisionConfig);
    private loggedInUserId: Signal<number | undefined> = this.#globalLoggedInUserStore.selectedUserId;

    dataMappingFormState = signalSlice({
        initialState: this.initialFormState,
        sources: [
            // As the form changes
            formCombined(this.divisionDataMappingForm),
            this.selectedDivisionConfig$.pipe(map(config => ({ selectedDivisionConfig: config }))),
            // On load/changes to the division
            state =>
                this.#formLifecycleService.initializeAndPatchForm(
                    state.selectedDivisionConfig$,
                    state.loaded$,
                    this.divisionDataMappingForm,
                    data => this.initializeEmptyMapperControls(data),
                    data => this.mapDivisionConfigToFormData(data)
                ),
            // Data for options retrieved on load
            zip(this.#dataMartService.getDataModel(), this.#salesforceService.getObjects()).pipe(
                map(([dataModel, objects]) => {
                    return {
                        salesforceObjectOptions: objects.sobjects,
                        dataMartTableOptions: dataModel.tables,
                    };
                })
            ),
            zip(this.selectedDivisionConfig$, this.#dataMartService.getDataModel()).pipe(
                map(([selectedDiv, dataModel]) => {
                    const { dataMartTable } = this.#formLifecycleService.configureDataMartData(selectedDiv, dataModel);
                    return {
                        dataMartTable,
                    };
                })
            ),
            this.selectedDivisionConfig$
                .pipe(
                    map(divConfig => {
                        const sfRecObject = divConfig?.config?.mapping?.salesforceRecommendationObject;
                        const sfPlayObject = divConfig?.config?.mapping?.salesforcePlayObject;
                        return {
                            sfRecObject,
                            sfPlayObject,
                        };
                    })
                )
                .pipe(
                    switchMap(recObjs => {
                        const calls = [];
                        if (recObjs.sfRecObject) {
                            calls.push(this.#salesforceService.getFieldsForObject(recObjs.sfRecObject));
                        } else {
                            calls.push(of());
                        }
                        if (recObjs.sfPlayObject) {
                            calls.push(this.#salesforceService.getFieldsForObject(recObjs.sfPlayObject));
                        } else {
                            calls.push(of());
                        }
                        return forkJoin(calls);
                    }),
                    map(([sfRecObjResponse, sfPlayObjResponse]) => {
                        return {
                            playFieldsOptions: sfPlayObjResponse,
                            recommendationFieldsOptions: sfRecObjResponse,
                        };
                    })
                ),
        ],
        selectors: state => ({
            dataMartFields: () =>
                state
                    .dataMartTableOptions()
                    .find(table => table.name === state()?.selectedDivisionConfig?.config?.mapping?.dataMartTable)
                    ?.fields ?? [],
        }),
    });

    public submit() {
        const state = this.dataMappingFormState;

        const divisionId = this.#globalLoggedInUserStore.selectedDivisionConfig()?.id;
        const datamartTable = state().dataMartTable;
        const configMapping = state().selectedDivisionConfig?.config?.mapping;
        const loggedInUserId = this.loggedInUserId();

        let submitted: Observable<{ submitted: boolean }>;

        if (divisionId && datamartTable && configMapping && loggedInUserId) {
            const uniqueSalesforceTargetFields: SalesforceUniqueFieldTarget[] =
                configMapping.uniqueSalesforceTargetFields ?? [];

            const relationsAndCriteriaTypes$ = this.#formLifecycleService.getRelationsAndCrtieriaTypes(
                uniqueSalesforceTargetFields.length,
                uniqueSalesforceTargetFields.map(field => field.sourceField.name),
                datamartTable.name,
                (sourceTableName: string, sourceColumnName: string) =>
                    this.#dataMartService.getRelations(sourceTableName, sourceColumnName)
            );

            const multipleRecommendationsTableMap$ = configMapping.multipleRecommendations?.enabled
                ? this.#dataMartService.getMultipleRecommendationsTableMap()
                : of(null);

            submitted = forkJoin([relationsAndCriteriaTypes$, multipleRecommendationsTableMap$]).pipe(
                switchMap(([_, __]) => {
                    const divisionConfig: RequiredDivisionConfigData = {
                        id: divisionId,
                        updatedBy: loggedInUserId,
                    };

                    const existingMapping = configMapping;
                    const newDataMappings = this.formatMappingForSave(
                        state(),
                        configMapping,
                        this.dataMappingFormState.dataMartFields()
                    );

                    const divisionConfigData: Pick<DivisionConfigData, 'mapping'> = {
                        mapping: {
                            ...existingMapping,
                            ...newDataMappings,
                        },
                    };

                    return from(
                        this.#globalDivisionStore.saveDivisionConfigAndData({
                            config: divisionConfig,
                            data: divisionConfigData,
                        })
                    ).pipe(map(() => ({ submitted: true })));
                })
            );
        } else {
            submitted = of({
                submitted: false,
            });
        }
        return lastValueFrom(submitted);
    }

    public addMapperRow(rowId: string | undefined, formArray: FormArray<FormGroup<FieldMappingForm>>) {
        const formState = this.dataMappingFormState;

        const mappingDataProvider = () => ({
            dataMartFields: formState.dataMartFields(),
            playSalesforceFields: formState.playFieldsOptions()?.fields ?? [],
            recommendationSalesforceFields: formState.recommendationFieldsOptions()?.fields ?? [],
            definedFields:
                formState.selectedDivisionConfig()?.config?.mapping?.additionalFields?.map(control => ({
                    id: control.id,
                    name: control.name,
                    type: control.type,
                    source: control.source,
                    required: control.required,
                    value: control.value,
                    picklistElements: control.picklistElements,
                })) ?? [],
        });

        formArray.push(
            this.#fb.group({
                id: this.#fb.control<string | undefined>(rowId),
                fieldType: this.#fb.control<'dataMart' | 'definedField' | 'predefinedField'>(
                    'dataMart',
                    Validators.required
                ),
                sourceField: this.#fb.control('', [
                    Validators.required,
                    this.#validators.mappedFieldDataTypeMisMatchValidator(mappingDataProvider),
                ]),
                mappingType: this.#fb.control<'play' | 'rec' | 'both' | undefined>(undefined, Validators.required),
                targetPlayField: this.#fb.control<string>('', {
                    validators: [
                        this.#validators.fieldMappingTypeValidator('play'),
                        this.#validators.mappedFieldDataTypeMisMatchValidator(mappingDataProvider),
                    ],
                }),
                targetRecommendationField: this.#fb.control<string>('', {
                    validators: [
                        this.#validators.fieldMappingTypeValidator('rec'),
                        this.#validators.mappedFieldDataTypeMisMatchValidator(mappingDataProvider),
                    ],
                }),
            })
        );
    }

    constructor() {
        effect(
            () => {
                this.#formLifecycleService.enableOrDisableWhenLoading(
                    this.dataMappingFormState.loaded(),
                    this.divisionDataMappingForm
                );
            },
            { debugName: 'form init + load' }
        );

        effect(
            () => {
                const formLoaded = this.dataMappingFormState.loaded();

                const dataMartFields = this.dataMappingFormState.dataMartFields();
                const playFieldsOptions = this.dataMappingFormState.playFieldsOptions();
                const recommendationFieldsOptions = this.dataMappingFormState.recommendationFieldsOptions();
                const fieldMappings = this.divisionDataMappingForm.controls.fieldMapping;

                if (formLoaded) {
                    this.#validators.triggerValidationOnMappingRows(
                        dataMartFields,
                        playFieldsOptions,
                        recommendationFieldsOptions,
                        fieldMappings
                    );
                }
            },
            { debugName: 'data availability validation' }
        );
    }

    /**
     * @description Step #1) Create empty form controls for X mapping fields of whatever type
     */
    private initializeEmptyMapperControls(divisionConfig: DivisionConfig) {
        const { fieldMappingFormValues } = this.formatConfigMappingData(divisionConfig);

        const generateFieldMappingsMappings = (fieldMappingForm: FormArray<FormGroup<FieldMappingForm>>) => {
            const fieldMappings = fieldMappingFormValues ?? [];
            if (fieldMappings.length === 0) {
                for (let i = 0; i < predefinedFields.filter(field => field.required).length; i++) {
                    this.addMapperRow(predefinedFields[i].id, fieldMappingForm);
                }
            } else {
                for (let i = 0; i < fieldMappings.length; i++) {
                    // for unrequired predefined fields, the ID will be overwritten on pick in the template anyways
                    this.addMapperRow(fieldMappings[i]?.id, fieldMappingForm);
                }
            }
        };

        generateFieldMappingsMappings(this.divisionDataMappingForm.controls.fieldMapping);
    }

    /**
     * @description Step #2) Shape data to form slice state, and mark `loaded: true`
     */
    private mapDivisionConfigToFormData(divisionConfig: DivisionConfig): {
        value: Partial<DataMappingFormValue>;
        status: FormControlStatus;
        loaded: boolean;
    } {
        const fieldMappingFormValues = this.formatConfigMappingData(divisionConfig);

        const value: Partial<DivisionDataMappingFormSliceState['value']> = {
            fieldMapping: fieldMappingFormValues.fieldMappingFormValues,
        };
        const statusAndLoading: Pick<DivisionDataMappingFormSliceState, 'status' | 'loaded'> = {
            status: this.divisionDataMappingForm.status,
            loaded: true,
        };
        return {
            value: value,
            status: statusAndLoading.status,
            loaded: statusAndLoading.loaded,
        };
    }

    /**
     * @description Step #1 and #2 helper
     */
    private formatConfigMappingData(divisionConfig: DivisionConfig) {
        // Maps over all the raw data, derives respective form state per each data mapping, then filters out rows already covered with previous transformation.
        const convertToFieldMappingFormValues = (
            fieldMappings: DivisionMappingRow[] | undefined,
            salesforcePlayObjectName?: string
        ): DivisionDataMappingFieldMappingFormValue[] => {
            const mappingType = (mapping: DivisionMappingRow) =>
                mapping.target.objectName === salesforcePlayObjectName ? 'play' : 'rec';

            const isAdditionalField = (arg: DataMartField | DefinedField | PredefinedField): arg is DefinedField =>
                !!(arg as DefinedField)?.type;

            const isDataMartField = (arg: DataMartField | DefinedField | PredefinedField): arg is DataMartField =>
                !!(arg as DataMartField)?.fieldType;

            const sourceFieldNameAndType = (
                source: DataMartField | DefinedField | PredefinedField
            ): { name: string; type: 'definedField' | 'dataMart' | 'predefinedField' } => {
                if (isAdditionalField(source)) {
                    return { name: source.id, type: 'definedField' };
                } else if (isDataMartField(source)) {
                    return { name: source.name, type: 'dataMart' };
                } else {
                    return { name: source?.id, type: 'predefinedField' };
                }
            };

            const idToFieldMappings: Map<string, DivisionMappingRow[]> = new Map();
            fieldMappings?.forEach(mapping => {
                if (idToFieldMappings.has(mapping.id)) {
                    idToFieldMappings.get(mapping.id)?.push(mapping);
                } else {
                    idToFieldMappings.set(mapping.id, [mapping]);
                }
            });

            const fieldMappingFormValueArray: DivisionDataMappingFieldMappingFormValue[] = [];

            idToFieldMappings.forEach((idToFieldMapping, id) => {
                const { name, type } = sourceFieldNameAndType(idToFieldMapping.at(0)!.source);

                const fieldMapping: DivisionDataMappingFieldMappingFormValue = {
                    id: id,
                    fieldType: type,
                    sourceField: name,
                    mappingType: idToFieldMapping.length > 1 ? 'both' : mappingType(idToFieldMapping.at(0)!),
                    targetPlayField: idToFieldMapping.find(row => mappingType(row) === 'play')?.target.name ?? '',
                    targetRecommendationField:
                        idToFieldMapping.find(row => mappingType(row) === 'rec')?.target.name ?? '',
                };
                fieldMappingFormValueArray.push(fieldMapping);
            });

            return fieldMappingFormValueArray;
        };

        const dataMapping: DivisionMappingSettings | undefined = divisionConfig?.config?.mapping;

        const uniqueSFTargetFields = dataMapping?.uniqueSalesforceTargetFields;

        const uniqueSalesforceTargetFields =
            uniqueSFTargetFields && uniqueSFTargetFields.length > 0
                ? uniqueSFTargetFields.map(field => {
                      return {
                          id: field.id,
                          name: field.name,
                          sourceField: field.sourceField.name,
                          targetField: field.targetField.name,
                      };
                  })
                : [];
        const additionalFields = dataMapping?.additionalFields ?? [];
        const fieldMappings = dataMapping?.fieldMappings ?? [];
        const fieldMappingFormValues = convertToFieldMappingFormValues(
            fieldMappings,
            divisionConfig.config?.mapping?.salesforcePlayObject
        );

        const requiredPredefinedFieldsMappedToForm: DivisionDataMappingFieldMappingFormValue[] = predefinedFields
            .filter(field => field.required)
            .map(field => ({
                fieldType: 'predefinedField',
                id: field.id,
                mappingType: undefined,
                sourceField: field.id,
                targetPlayField: '',
                targetRecommendationField: '',
            }));

        const combinedFieldMappingFormValues = fieldMappingFormValues.concat(
            fieldMappings?.length === 0 ? requiredPredefinedFieldsMappedToForm : []
        );

        return {
            uniqueSalesforceTargetFields: uniqueSalesforceTargetFields ?? [],
            additionalFields: additionalFields,
            fieldMappingFormValues: combinedFieldMappingFormValues,
        };
    }

    /**
     * @description Maps over all the form data, derives respective server state, while
     *                  splitting up "both" fieldMapping into separate rows
     */
    private formatMappingForSave(
        state: DivisionDataMappingFormSliceState,
        configMapping: DivisionMappingSettings,
        dataMartFields: DataMartField[]
    ): DivisionDataMappingSettings {
        const splitBothsIntoPlaysAndRecs = (
            formRow: DivisionDataMappingFieldMappingFormValue
        ): DivisionDataMappingFieldMappingFormValue[] => {
            if (formRow.targetPlayField !== '' && formRow.targetRecommendationField !== '') {
                const playMapping: DivisionDataMappingFieldMappingFormValue = structuredClone(formRow);
                const recMapping: DivisionDataMappingFieldMappingFormValue = structuredClone(formRow);

                playMapping.targetRecommendationField = '';
                recMapping.targetPlayField = '';
                return [playMapping, recMapping];
            } else {
                return [formRow];
            }
        };

        const combineTransformedMappingRows = (sliceState: DivisionDataMappingFormSliceState): DivisionMappingRow[] => {
            const combinedFormArray: DivisionDataMappingFieldMappingFormValue[][] = [];
            sliceState.value.fieldMapping.forEach(mapping => {
                combinedFormArray.push(splitBothsIntoPlaysAndRecs(mapping));
            });

            const flattenedCombinedFormArray = combinedFormArray.flat();
            return flattenedCombinedFormArray.map(item => {
                return this.formatServerMappedRowForSave(sliceState, item, dataMartFields, configMapping);
            });
        };

        return {
            fieldMappings: combineTransformedMappingRows(state),
        };
    }

    private formatServerMappedRowForSave(
        sliceState: DivisionDataMappingFormSliceState,
        formRow: DivisionDataMappingFieldMappingFormValue,
        dataMartFields: DataMartField[],
        configMapping: DivisionMappingSettings
    ): DivisionMappingRow {
        const fullSourceField = (sourceField: string): DataMartField | DefinedField | PredefinedField => {
            let field: DataMartField | DefinedField | PredefinedField | undefined;
            if (formRow.fieldType === 'dataMart') {
                field = {
                    ...dataMartFields.find(dataMartField => {
                        return dataMartField.name === sourceField;
                    })!,
                    tableName: configMapping.dataMartTable,
                };
            } else if (formRow.fieldType === 'definedField') {
                const additionalFields = configMapping.additionalFields ?? [];
                field = additionalFields.find(additionalField => {
                    return (
                        additionalField.id ===
                        additionalFields.find(stateAdditField => stateAdditField.id === sourceField)?.id
                    );
                })!;
            } else {
                const matchingIndex = sliceState.value.fieldMapping.findIndex(fieldMapping => {
                    return fieldMapping.sourceField === sourceField;
                });
                const matchingField = sliceState.value.fieldMapping[matchingIndex];
                field = {
                    id: matchingField.sourceField as PredefinedField['id'],
                    name: matchingField.sourceField as PredefinedField['name'],
                    required: matchingIndex <= 3,
                } as PredefinedField;
            }
            return field;
        };

        const fullSalesforceField = (targetSalesforceFieldName: string, type: 'play' | 'rec'): SalesforceField => {
            let field: SalesforceField | undefined;

            if (type === 'play') {
                const fieldMetadata = sliceState.playFieldsOptions?.fields.find(
                    playFieldsOptionField => playFieldsOptionField.name === targetSalesforceFieldName
                );
                field = {
                    objectName: configMapping.salesforcePlayObject,
                    label: fieldMetadata?.label ?? '',
                    type: fieldMetadata?.type ?? '',
                    name: fieldMetadata?.name ?? '',
                };
            } else {
                const fieldMetadata = sliceState.recommendationFieldsOptions?.fields.find(
                    recommendationFieldsOptionField =>
                        recommendationFieldsOptionField.name === targetSalesforceFieldName
                );
                field = {
                    objectName: configMapping.salesforceRecommendationObject,
                    label: fieldMetadata?.label ?? '',
                    type: fieldMetadata?.type ?? '',
                    name: fieldMetadata?.name ?? '',
                };
            }
            return field;
        };

        const finalRowWithoutTypeField: DivisionMappingRow = {
            mappingRowType: formRow.fieldType,
            id: formRow.id ?? '',
            source: fullSourceField(formRow.sourceField),
            target:
                formRow.targetPlayField !== ''
                    ? fullSalesforceField(formRow.targetPlayField, 'play')
                    : fullSalesforceField(formRow.targetRecommendationField, 'rec'),
            salesforceField:
                formRow.targetPlayField !== ''
                    ? fullSalesforceField(formRow.targetPlayField, 'play')
                    : fullSalesforceField(formRow.targetRecommendationField, 'rec'),
        };

        if (formRow.fieldType === 'dataMart') {
            finalRowWithoutTypeField.dataMartField = fullSourceField(formRow.sourceField) as DataMartField;
        } else if (formRow.fieldType === 'definedField') {
            finalRowWithoutTypeField.definedField = fullSourceField(formRow.sourceField) as DefinedField;
        } else if (formRow.fieldType === 'predefinedField') {
            finalRowWithoutTypeField.predefinedField = fullSourceField(formRow.sourceField) as PredefinedField;
        }
        return finalRowWithoutTypeField;
    }
}
